import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth, type AccountRole } from "@/hooks/useAuth";
import AvatarPicker from "./AvatarPicker";

export default function ProfileForm() {
  const { user, profile, refreshProfile } = useAuth();
  const [fullName, setFullName] = useState(profile?.full_name || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [location, setLocation] = useState(profile?.location || "");
  const [bio, setBio] = useState(profile?.bio || "");
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || "");
  const [role, setRole] = useState<AccountRole>((profile?.role as AccountRole) || "rider");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [message, setMessage] = useState("");

  const email = profile?.email || user?.email || "";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;
    if (!fullName.trim()) {
      setStatus("error");
      setMessage("Please add your name so hosts know who they're meeting.");
      return;
    }
    if (phone && !/^[+\d\s()-]{7,}$/.test(phone.trim())) {
      setStatus("error");
      setMessage("That phone number doesn't look right — check it and try again.");
      return;
    }

    setStatus("saving");
    setMessage("");
    try {
      const { error } = await supabase
        .from("profiles")
        .upsert(
          {
            id: user.id,
            full_name: fullName.trim(),
            email,
            phone: phone.trim() || null,
            location: location.trim() || null,
            bio: bio.trim() || null,
            avatar_url: avatarUrl || null,
            role,
            is_driver: role === "host",
          },
          { onConflict: "id" }
        );
      if (error) throw error;
      await refreshProfile();
      setStatus("saved");
      setMessage("Your profile is updated.");
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "We couldn't save your profile. Please try again.");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl surface-card p-6">
      <div className="flex items-center gap-3 border-b border-background-200/70 pb-5">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/12 text-xl text-primary-400">
          <i className="ri-user-settings-line"></i>
        </span>
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground-950">Your details</h2>
          <p className="text-xs text-foreground-500">Hosts and riders see this when you book.</p>
        </div>
      </div>

      <div className="mt-6">
        <AvatarPicker name={fullName} value={avatarUrl} onChange={setAvatarUrl} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="settings-name" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
            Full name
          </label>
          <input
            id="settings-name"
            type="text"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            placeholder="e.g. Wanjiku Mwangi"
            className="w-full rounded-lg border border-background-200/70 bg-background-50 px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="settings-phone" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
            Phone number
          </label>
          <input
            id="settings-phone"
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="0712 345 678"
            autoComplete="tel"
            className="w-full rounded-lg border border-background-200/70 bg-background-50 px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="settings-email" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
            Email
          </label>
          <input
            id="settings-email"
            type="email"
            value={email}
            readOnly
            className="w-full cursor-not-allowed rounded-lg border border-background-200/70 bg-background-200/30 px-3.5 py-3 text-sm text-foreground-500"
          />
        </div>

        <div>
          <label htmlFor="settings-location" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
            City / neighbourhood
          </label>
          <input
            id="settings-location"
            type="text"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            placeholder="e.g. Kilimani, Nairobi"
            className="w-full rounded-lg border border-background-200/70 bg-background-50 px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-5">
        <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">I&apos;m here to</span>
        <div className="inline-flex rounded-full bg-background-100 p-1">
          {(["rider", "host"] as AccountRole[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setRole(option)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${
                role === option ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              <i className={option === "host" ? "ri-hand-coin-line" : "ri-steering-2-line"}></i>
              {option === "host" ? "Host a car" : "Rent a car"}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="settings-bio" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
          About you
        </label>
        <textarea
          id="settings-bio"
          value={bio}
          onChange={(event) => setBio(event.target.value.slice(0, 400))}
          rows={3}
          maxLength={400}
          placeholder="Tell riders or hosts a little about yourself — it helps build trust."
          className="w-full resize-none rounded-lg border border-background-200/70 bg-background-50 px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
        />
        <p className="mt-1 text-right text-xs text-foreground-500">{bio.length}/400</p>
      </div>

      {status === "error" && message && (
        <p className="mt-5 flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-3 text-sm text-secondary-300">
          <i className="ri-error-warning-line"></i> {message}
        </p>
      )}
      {status === "saved" && message && (
        <p className="mt-5 flex items-center gap-2 rounded-lg border border-accent-500/30 bg-accent-500/10 px-3.5 py-3 text-sm text-accent-300">
          <i className="ri-checkbox-circle-fill"></i> {message}
        </p>
      )}

      <div className="mt-6 flex items-center gap-3 border-t border-background-200/70 pt-5">
        <button
          type="submit"
          disabled={status === "saving"}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50"
        >
          {status === "saving" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> Saving
            </>
          ) : (
            <>
              <i className="ri-save-line"></i> Save changes
            </>
          )}
        </button>
        <Link
          to="/trips"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
        >
          <i className="ri-roadster-line"></i> My trips
        </Link>
      </div>
    </form>
  );
}